import React, { useState, useRef, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { Topbar } from '@/components/shared/Topbar'
import { Atmosphere } from '@/components/shared/Atmosphere'
import { QUIZ_QUESTIONS } from '@/data/destinations'
import type { QuizAnswer } from '@/types'

interface ChatBubble {
  id: string
  role: 'assistant' | 'user'
  content: string
  questionIndex?: number
}

// Yonder's guide persona — upbeat, parent-friendly, snappy pacing (150–300ms).
const GUIDE_INTRO = `Hi! I'm <strong>Yonder</strong> ✦ — I find where families belong. Quick one: it's the first morning of half-term…`

// One short scenario-flavour sentence per question, in Yonder's playful voice.
// These sit ABOVE the existing question intro/question text from the data file.
const SCENARIO_LINES = [
  "Six whole days off school and the group chat is already going wild with ideas.",
  "Think back to your last brilliant holiday — the bit the kids replay on repeat.",
  "You're the one holding the family calendar and the family budget. No pressure!",
  "Picture the first hour of any holiday morning, from bleary eyes to breakfast.",
  "Fast-forward ten years. The kids are grown and swapping holiday stories.",
]

function ThinkingDots() {
  return (
    <div className="flex gap-1.5 items-center py-1">
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="w-2 h-2 rounded-full"
          style={{ background: i === 0 ? '#FF9500' : i === 1 ? '#3DDBD9' : '#FFD166' }}
          animate={{ y: [0, -7, 0] }}
          transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.18 }}
        />
      ))}
    </div>
  )
}

export function QuizScreen() {
  const { addAnswer, setScreen } = useAppStore()
  const [currentQ, setCurrentQ] = useState(0)
  const [bubbles, setBubbles] = useState<ChatBubble[]>([])
  const [isThinking, setIsThinking] = useState(false)
  const [showOptions, setShowOptions] = useState(false)
  const [inputVal, setInputVal] = useState('')
  const [introDone, setIntroDone] = useState(false)
  const chatEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  const pushQuestion = useCallback((qIndex: number) => {
    if (qIndex >= QUIZ_QUESTIONS.length) return
    const q = QUIZ_QUESTIONS[qIndex]
    setIsThinking(true)
    setShowOptions(false)

    setTimeout(() => {
      setIsThinking(false)
      const scenario = SCENARIO_LINES[qIndex]
      const questionText = q.question ? `${q.intro}<br/><br/>${q.question}` : q.intro
      const content = `<span class="scenario-line">${scenario}</span><br/><br/>${questionText}`
      setBubbles((prev) => [
        ...prev,
        { id: `q-${qIndex}`, role: 'assistant', content, questionIndex: qIndex },
      ])
      setShowOptions(true)
      setTimeout(scrollToBottom, 100)
    }, 260) // Yonder snappy pacing
  }, [])

  // Guide persona intro bubble on entry: typing dots first, then the intro types in.
  useEffect(() => {
    setIsThinking(true)
    const t = setTimeout(() => {
      setIsThinking(false)
      setBubbles([{ id: 'guide-intro', role: 'assistant', content: GUIDE_INTRO }])
      setIntroDone(true)
      setTimeout(scrollToBottom, 100)
    }, 500)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    if (introDone) pushQuestion(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introDone])

  useEffect(() => {
    scrollToBottom()
  }, [bubbles, isThinking])

  const handleAnswer = (emoji: string, label: string) => {
    const answer: QuizAnswer = { emoji, label, questionIndex: currentQ }
    addAnswer(answer)

    // Dim options, add user bubble
    setShowOptions(false)
    setBubbles((prev) => [
      ...prev,
      { id: `a-${currentQ}`, role: 'user', content: `${emoji} ${label}` },
    ])

    const nextQ = currentQ + 1
    if (nextQ >= QUIZ_QUESTIONS.length) {
      // Quiz complete
      setTimeout(() => {
        setIsThinking(true)
        setTimeout(() => {
          setIsThinking(false)
          setBubbles((prev) => [
            ...prev,
            {
              id: 'complete',
              role: 'assistant',
              content: '🎉 <strong>Amazing!</strong> I\'ve decoded your family\'s Travel DNA. Your perfect destinations are ready to explore!',
            },
          ])
          setTimeout(() => setScreen('dna'), 700)
        }, 500)
      }, 250)
    } else {
      setCurrentQ(nextQ)
      setTimeout(() => pushQuestion(nextQ), 250)
    }
  }

  const handleSend = () => {
    if (!inputVal.trim()) return
    // Free-text answers are stored as the user's answerLabel (used in the
    // Claude prompt) but map to the FIRST option's emoji for DNA scoring —
    // so a typed answer still contributes to the family's travel DNA.
    const firstOption = QUIZ_QUESTIONS[currentQ]?.options[0]
    const emoji = firstOption?.emoji ?? '💬'
    handleAnswer(emoji, inputVal.trim())
    setInputVal('')
  }

  const progress = currentQ + 1
  const currentOptions = showOptions && currentQ < QUIZ_QUESTIONS.length
    ? QUIZ_QUESTIONS[currentQ].options
    : []

  return (
    <div className="yonder-world min-h-screen flex flex-col">
      <Atmosphere density="low" />
      <Topbar
        centerContent={<span className="bg-sun-pale/20 border border-sun-pale/40 text-sun-pale font-black px-3 py-1 rounded-full">{Math.min(progress, QUIZ_QUESTIONS.length)} of {QUIZ_QUESTIONS.length}</span>}
        backTo="mode"
      />

      {/* Segmented progress bar (5 segments) */}
      <div className="relative z-10 flex gap-1 px-3 pt-3 pb-1">
        {QUIZ_QUESTIONS.map((_, i) => (
          <div key={i} className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-sun to-sun-light rounded-full shadow-glow-sun"
              initial={{ width: 0 }}
              animate={{ width: i < progress ? '100%' : '0%' }}
              transition={{ duration: 0.25 }}
            />
          </div>
        ))}
      </div>

      {/* Chat area */}
      <div className="relative z-10 flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3 max-w-2xl mx-auto w-full">
        <AnimatePresence initial={false}>
          {bubbles.map((bubble) => (
            <motion.div
              key={bubble.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className={`flex gap-2.5 ${bubble.role === 'user' ? 'justify-end' : 'items-start'}`}
            >
              {bubble.role === 'assistant' && (
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sun to-sun-light flex items-center justify-center text-lg shadow-glow-sun flex-shrink-0">
                  🧭
                </div>
              )}
              {bubble.role === 'assistant' ? (
                <div
                  className="ai-bubble yonder-cream-card rounded-tl-sm rounded-tr-2xl rounded-bl-2xl rounded-br-2xl px-4 py-3 max-w-[88%] text-[13px] font-semibold leading-relaxed shadow-lg"
                  dangerouslySetInnerHTML={{ __html: bubble.content }}
                />
              ) : (
                <div className="bg-gradient-to-br from-sun to-sun-light text-white font-bold text-[13px] rounded-tl-2xl rounded-tr-sm rounded-bl-2xl rounded-br-2xl px-4 py-2.5 max-w-[76%] shadow-glow-sun">
                  {bubble.content}
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Thinking indicator */}
        {isThinking && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-start gap-2.5"
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sun to-sun-light flex items-center justify-center text-lg shadow-glow-sun flex-shrink-0">
              🧭
            </div>
            <div className="yonder-cream-card rounded-tl-sm rounded-2xl px-4 py-3 shadow-lg">
              <ThinkingDots />
            </div>
          </motion.div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Options */}
      <AnimatePresence>
        {currentOptions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="relative z-10 px-4 pb-2 max-w-2xl mx-auto w-full"
          >
            <div className="grid grid-cols-2 gap-2">
              {currentOptions.map((opt, i) => (
                <motion.button
                  key={i}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => handleAnswer(opt.emoji, opt.label)}
                  className="flex items-start gap-2 text-left border-2 border-white/15 rounded-2xl px-3 py-2.5 text-[12px] font-bold text-white/80 bg-white/5 backdrop-blur-sm hover:border-sun hover:bg-white/10 hover:text-sun-pale active:scale-[0.97] transition-all"
                >
                  <span className="text-lg flex-shrink-0 mt-0.5">{opt.emoji}</span>
                  <span className="leading-snug">{opt.label}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Text input */}
      <div className="relative z-10 px-4 py-3 bg-black/20 border-t border-white/10 backdrop-blur-sm flex gap-2 items-center max-w-2xl mx-auto w-full">
        <input
          ref={inputRef}
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          placeholder="Type freely, or tap a choice above…"
          className="flex-1 bg-white/10 border-2 border-white/10 rounded-full px-4 py-2.5 text-[13px] font-semibold text-white placeholder-white/40 outline-none focus:border-sun focus:bg-white/15 transition-all"
        />
        <button
          onClick={handleSend}
          className="w-10 h-10 rounded-full bg-gradient-to-br from-sun to-sun-light text-white font-black text-lg flex items-center justify-center shadow-glow-sun hover:shadow-sun-lg active:scale-95 transition-all flex-shrink-0"
        >
          →
        </button>
      </div>
    </div>
  )
}
